import { useSchemaOrg } from '@unhead/schema-org/react';

/**
 * @type {import('$types/components/partials/markets').MarketSchema}
 */

const MarketSchema = ({ page, url, offices }) => {
    const schemas = [];

    // WebPage Schema
    const webPageSchema = {
        '@type': 'WebPage',
        name: page?.title,
        url: url,
    };

    // Add description if available
    if (page?.searchDescription) {
        webPageSchema.description = page.searchDescription;
    }

    // Add image if available
    if (page?.coverImage?.src) {
        webPageSchema.image = page.coverImage.src;
    }

    // Add breadcrumb if parent exists
    if (page?.parent) {
        webPageSchema.breadcrumb = {
            '@type': 'BreadcrumbList',
            itemListElement: [
                {
                    '@type': 'ListItem',
                    position: 1,
                    name: 'Home',
                    item: url?.split('/').slice(0, 3).join('/')
                }
            ]
        };

        if (page.parent.title) {
            webPageSchema.breadcrumb.itemListElement.push({
                '@type': 'ListItem',
                position: 2,
                name: page.parent.title,
                item: page.parent.relativeUrl
                    ? `${url?.split('/').slice(0, 3).join('/')}${page.parent.relativeUrl}`
                    : url?.split('/').slice(0, 3).join('/')
            });
        }

        webPageSchema.breadcrumb.itemListElement.push({
            '@type': 'ListItem',
            position: webPageSchema.breadcrumb.itemListElement.length + 1,
            name: page.title,
            item: url
        });
    }

    // Add publisher (RealEstateAgent)
    webPageSchema.publisher = {
        '@type': 'RealEstateAgent',
        name: 'Beauchamp Estates',
        url: url?.split('/').slice(0, 3).join('/')
    };

    // Main Entity: Place
    const placeSchema = {
        '@type': 'Place',
        name: page?.title,
        address: {
            '@type': 'PostalAddress',
            addressLocality: page?.title
        }
    };

    // Add description to place
    if (page?.searchDescription) {
        placeSchema.description = page.searchDescription;
    }

    // Add image to place
    if (page?.coverImage?.src) {
        placeSchema.image = page.coverImage.src;
    }

    // Add offices as containsPlace
    if (offices?.edges?.length > 0) {
        placeSchema.containsPlace = offices.edges
            .filter(edge => edge?.node)
            .map(edge => {
                const office = edge.node;
                const containedPlace = {
                    '@type': 'RealEstateAgent',
                    name: office.title
                };

                if (office.address) {
                    const plainAddress = office.address.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
                    containedPlace.address = {
                        '@type': 'PostalAddress',
                        name: plainAddress
                    };
                }

                if (office.coordinates?.lat && office.coordinates?.lng) {
                    containedPlace.geo = {
                        '@type': 'GeoCoordinates',
                        latitude: office.coordinates.lat,
                        longitude: office.coordinates.lng
                    };
                }

                if (office.relativeUrl) {
                    containedPlace.url = `${url?.split('/').slice(0, 3).join('/')}${office.relativeUrl}`;
                }

                return containedPlace;
            });

        // If we have offices with coordinates, use the first one for the main place geo
        const firstOfficeWithCoords = offices.edges.find(
            edge => edge?.node?.coordinates?.lat && edge?.node?.coordinates?.lng
        );
        if (firstOfficeWithCoords) {
            placeSchema.geo = {
                '@type': 'GeoCoordinates',
                latitude: firstOfficeWithCoords.node.coordinates.lat,
                longitude: firstOfficeWithCoords.node.coordinates.lng
            };
        }
    }

    webPageSchema.mainEntity = placeSchema;

    schemas.push(webPageSchema);

    useSchemaOrg(page ? schemas : [], { tagPosition: 'head' });

    return null;
};

export default MarketSchema;
